import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'
import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditRelatedEntityModal from '@modals/CreateOrEditRelatedEntityModal'
import CreateOrEditTaskListModal from '@modals/CreateOrEditTaskListModal'
import CreateOrEditTaskListSectionModal from '@modals/CreateOrEditTaskListSectionModal'
import CreateOrEditTrackingLinkModal from '@modals/CreateOrEditTrackingLinkModal'
import ManageActivityModal from '@modals/ManageActivityModal/ManageActivityModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'

import defaultRequestOptions from '@sections/Admin/packs/Projects/defaultRequestOptions'

import type {
  ActivityModel, RelatedEntityModel, TaskListModel, TaskListSectionModel, TrackingLinkModel,
} from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    deleteAttachment: (payload: HandleCallbackActionParams) => void,
    deleteRelatedEntity: (payload: HandleCallbackActionParams) => void,
    deleteTrackingLink: (payload: HandleCallbackActionParams) => void,
    showCreateOrEditRelatedEntityModal: (payload: object) => void,
    showCreateOrEditTaskListSectionModal: (payload: object) => void,
    showCreateOrEditTrackingLinkModal: (payload: object) => void,
    showManageActivityModal: (payload: object) => void,
    showUploadEntityAssetsModal: (payload: object) => void,
  },
}

const defaultState = {
  showCreateOrEditRelatedEntityModal: false,
  showCreateOrEditTaskListModal: false,
  showCreateOrEditTaskListSectionModal: false,
  showCreateOrEditTrackingLinkModal: false,
  showManageActivityModal: false,
  showUploadEntityAssetsModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditRelatedEntityModal: {
      closeModal: () => setState({ showCreateOrEditRelatedEntityModal: false }),
      createRelatedEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateRelatedEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTaskListModal: {
      closeModal: () => setState({ showCreateOrEditTaskListModal: false }),
      toggleTaskListCompletion: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTaskList: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTaskListSectionModal: {
      closeModal: () => setState({ showCreateOrEditTaskListSectionModal: false }),
      createTaskListSection: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTaskListSection: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTrackingLinkModal: {
      closeModal: () => setState({ showCreateOrEditTrackingLinkModal: false }),
      createTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Overview = () => {
  const { taskListId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditRelatedEntityModal,
    showCreateOrEditTaskListModal,
    showCreateOrEditTaskListSectionModal,
    showCreateOrEditTrackingLinkModal,
    showManageActivityModal,
    showUploadEntityAssetsModal,
  } = state

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteRelatedEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showCreateOrEditRelatedEntityModal: (payload: {relatedEntity: RelatedEntityModel}) => {
        setModalData('CreateOrEditRelatedEntityModal', payload)
        setState({ showCreateOrEditRelatedEntityModal: true })
      },
      showCreateOrEditTaskListModal: (payload: { taskList: TaskListModel }) => {
        setModalData('CreateOrEditTaskListModal', payload)
        setState({ showCreateOrEditTaskListModal: true })
      },
      showCreateOrEditTaskListSectionModal: (payload: { taskListSection: TaskListSectionModel }) => {
        setModalData('CreateOrEditTaskListSectionModal', payload)
        setState({ showCreateOrEditTaskListSectionModal: true })
      },
      showCreateOrEditTrackingLinkModal: (payload: { trackingLink: TrackingLinkModel }) => {
        setModalData('CreateOrEditTrackingLinkModal', payload)
        setState({ showCreateOrEditTrackingLinkModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showUploadEntityAssetsModal: (payload: object) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent taskListId={taskListId} />

        <CreateOrEditRelatedEntityModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditRelatedEntityModal', setState)}
          showModal={showCreateOrEditRelatedEntityModal}
        />

        <CreateOrEditTaskListModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditTaskListModal', setState)}
          requestOptions={{
            ...defaultRequestOptions.taskList,
            include_related_entity_subject: true,
            include_task_list_related_entities: true,
            include_task_list_sections: true,
            include_task_list_task_counts: true,
          }}
          showModal={showCreateOrEditTaskListModal}
        />

        <CreateOrEditTaskListSectionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditTaskListSectionModal', setState)}
          showModal={showCreateOrEditTaskListSectionModal}
        />

        <CreateOrEditTrackingLinkModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditTrackingLinkModal', setState)}
          showModal={showCreateOrEditTrackingLinkModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <UploadEntityAssetsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Overview
